import './i18n/i18n'; // i18next MUST init before React renders
import { Suspense, useEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import { BrowserRouter as Router } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { store, persistor } from './store'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CircularProgress } from '@mui/material'
import { theme } from 'globalStyles.css'
import { useTranslation } from 'react-i18next'
import { ptBR as muiPtBR, enUS as muiEnUS, esES as muiEsES } from '@mui/material/locale'
import { ptBR as datesPtBR, enUS as datesEnUS, esES as datesEsES } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/es'

const MUI_LOCALES = { pt: muiPtBR, en: muiEnUS, es: muiEsES }
const DATE_LOCALES = { pt: datesPtBR, en: datesEnUS, es: datesEsES }
const DAYJS_LOCALES = { pt: 'pt-br', en: 'en', es: 'es' }

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      cacheTime: 0,
    },
  },
  logger: {
    log: console.log,
    warn: console.warn,
    // ✅ no more errors on the console
    error: () => { },
  }
})

// Locale-aware ThemeProvider: re-renders when language changes
const LocaleAwareApp = () => {
  const { i18n } = useTranslation()
  const lang = i18n.language || 'pt'

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALES[lang] || 'pt-br')
  }, [lang])

  const localizedTheme = useMemo(() => {
    return createTheme(theme, MUI_LOCALES[lang] || muiPtBR, DATE_LOCALES[lang] || datesPtBR)
  }, [lang])

  return (
    <ThemeProvider theme={localizedTheme}>
      <App />
    </ThemeProvider>
  )
}

const rootElement = document.getElementById('root')
const root = createRoot(rootElement)

root.render(
  <Provider store={store}>
    <PersistGate persistor={persistor}>
      <QueryClientProvider client={client}>
        <Router>
          <Suspense fallback={<CircularProgress />}>
            <LocaleAwareApp />
          </Suspense>
        </Router>
      </QueryClientProvider>
    </PersistGate>
  </Provider>,
)
